import './Intro.sass';

import React, { FC, useCallback, useEffect, useState } from 'react';
import { animated, useSpring } from '@react-spring/web';
import { useRouter } from 'react-router5';
import { useSelector } from 'react-redux';

import { Button } from '$uikit';
import { SUBSCRIPTION_ADULT, SUBSCRIPTION_MEDIA } from '$shared/constants/pages';
import { useDesktop, useTranslator } from '$hooks';
import { statReachGoal } from '$utils';
import { contentPrioritySelector } from '$store/main';
import { ContentPriorityType } from '$store/main/interface';

export const Intro: FC = () => {
  const router = useRouter();
  const isDesktop = useDesktop();
  const t = useTranslator();
  const [{ x }, api] = useSpring(() => ({ x: 0 }));
  const [width, setWidth] = useState(0);
  const contentPriority = useSelector(contentPrioritySelector);
  const isAdultPriority = contentPriority === ContentPriorityType.ADULT;

  useEffect(() => {
    statReachGoal('intro_show');

    const wrap: HTMLDivElement | null = document.querySelector('.Intro');
    if (wrap) {
      setWidth(wrap.offsetWidth);
    } else {
      setWidth(isDesktop ? 600 : window.innerWidth);
    }
  }, []);

  const handleNext = useCallback(() => {
    api.start({
      x: width,
      config: {
        duration: isDesktop ? 300 : 200,
      },
    });
    statReachGoal('intro_step2');
  }, [api, width, isDesktop]);

  const handleStart = useCallback(() => {
    if (isAdultPriority) {
      router.navigate(
        SUBSCRIPTION_ADULT,
        {},
        {
          replace: true,
        },
      );
    } else {
      router.navigate(
        SUBSCRIPTION_MEDIA,
        {},
        {
          replace: true,
        },
      );
    }
    statReachGoal('intro_done');
  }, [router, isAdultPriority]);

  return (
    <div className="Intro">
      <animated.div
        className="Intro__scenes"
        style={{
          transform: x.interpolate((val) => `translateX(-${val}px)`),
        }}
      >
        <div className="Intro__scene" style={{ width }}>
          <div className="Intro__scene__content">
            <div
              className="Intro__scene__icon"
              style={{
                backgroundImage: `url(${
                  isAdultPriority
                    ? require('$assets/120/ic_discover_120_gradient.svg').default
                    : require('$assets/120/ic_fav_blue_120_gradient.svg').default
                })`,
              }}
            />
            <div className="Intro__scene__title">
              {t(!isAdultPriority ? 'intro_media_step1_title' : 'intro_step1_title')}
            </div>
            <div className="Intro__scene__caption">
              {t(!isAdultPriority ? 'intro_media_step1_caption' : 'intro_step1_caption')}
            </div>
          </div>
          <div className="Intro__scene__bottom">
            <Button stretched onClick={handleNext}>
              {t('intro_step1_button')}
            </Button>
          </div>
        </div>
        <div className="Intro__scene" style={{ width }}>
          <div className="Intro__scene__content">
            <div
              className="Intro__scene__icon"
              style={{
                backgroundImage: `url(${
                  isAdultPriority
                    ? require('$assets/120/ic_fav_120_gradient.svg').default
                    : require('$assets/120/ic_messages_120_gradient.svg').default
                })`,
              }}
            />
            <div className="Intro__scene__title">
              {t(!isAdultPriority ? 'intro_media_step2_title' : 'intro_step2_title')}
            </div>
            <div className="Intro__scene__caption">
              {t(!isAdultPriority ? 'intro_media_step2_caption' : 'intro_step2_caption')}
            </div>
          </div>
          <div className="Intro__scene__bottom">
            <Button stretched onClick={handleStart}>
              {t('intro_step2_button')}
            </Button>
          </div>
        </div>
      </animated.div>
    </div>
  );
};
